import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc, Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as JobDetailRoute } from '@/routes/_app/_auth/dashboard/_layout.jobs.$jobId'
import { useDoubleCheck } from '@/shared/hooks/use-double-check'

type CreateFromTemplateDialogState =
  | { readonly status: 'closed' }
  | {
      readonly status: 'open'
      readonly templateId: Id<'jobTemplates'>
      readonly templateName: string
    }

const TemplateCard = ({ template }: { template: Doc<'jobTemplates'> }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dialogState, setDialogState] = useState<CreateFromTemplateDialogState>(
    { status: 'closed' },
  )
  const [titleInput, setTitleInput] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const { doubleCheck, getButtonProps } = useDoubleCheck()

  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.job_templates.deleteMyJobTemplate,
    ),
  })
  const { mutateAsync: createFromTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.job_templates.createMyJobFromTemplate,
    ),
  })

  const handleOpenCreateDialog = (): void => {
    setTitleInput(template.name)
    setDialogState({
      status: 'open',
      templateId: template._id,
      templateName: template.name,
    })
  }

  const handleCreateFromTemplate = async (): Promise<void> => {
    if (dialogState.status !== 'open' || !titleInput.trim()) return
    setIsCreating(true)
    const jobId = await createFromTemplate({
      templateId: dialogState.templateId,
      title: titleInput.trim(),
    })
    setIsCreating(false)
    setDialogState({ status: 'closed' })
    navigate({
      to: JobDetailRoute.fullPath,
      params: { jobId: jobId as Id<'jobs'> },
    })
  }

  return (
    <>
      {dialogState.status === 'open' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-border bg-card p-6 shadow-lg">
            <h3 className="text-base font-semibold text-primary">
              {t('templates.createFromTemplateTitle', {
                name: dialogState.templateName,
              })}
            </h3>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                {t('jobs.form.titleLabel')}
              </label>
              <Input
                autoFocus
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFromTemplate()
                  if (e.key === 'Escape') setDialogState({ status: 'closed' })
                }}
                placeholder={t('jobs.form.titlePlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialogState({ status: 'closed' })}
              >
                {t('jobs.form.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleCreateFromTemplate}
                disabled={isCreating || !titleInput.trim()}
              >
                {isCreating ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  t('templates.createJobButton')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-sm font-medium text-primary truncate">
              {template.name}
            </p>
            <p className="line-clamp-2 text-xs text-primary/60">
              {template.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-md bg-secondary px-3 py-2">
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-sm font-semibold text-primary">
              {template.questions.length}
            </span>
            <span className="text-xs text-primary/50">
              {t('templates.questionCount')}
            </span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-sm font-semibold text-primary">
              {template.screenerQuestions.length}
            </span>
            <span className="text-xs text-primary/50">
              {t('templates.screenerCount')}
            </span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-sm font-semibold text-primary">
              {template.interviewCount}
            </span>
            <span className="text-xs text-primary/50">
              {t('templates.interviewCount')}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleOpenCreateDialog}>
            <Plus className="h-3.5 w-3.5" />
            {t('templates.useTemplate')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            {...getButtonProps({
              onClick: doubleCheck
                ? () => deleteTemplate({ templateId: template._id })
                : undefined,
            })}
          >
            {doubleCheck ? (
              t('jobs.deleteConfirm')
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
    </>
  )
}

export const JobTemplatesPage = () => {
  const { t } = useTranslation()
  const { data: templates = [] } = useQuery(
    convexQuery(api.features.jobs.job_templates.listMyJobTemplates, {}),
  )

  return (
    <div className="flex h-full w-full px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <p className="text-lg font-medium text-primary/60">
              {t('templates.empty')}
            </p>
            <p className="text-sm text-primary/40">
              {t('templates.emptyDescription')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <TemplateCard key={template._id} template={template} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
